import type { ContextuneSnapshot } from "../types.js";

/**
 * Serialises a ContextuneSnapshot to TOON (Token-Oriented Object Notation).
 *
 * Flat objects (marketing_params, device_info) render as YAML-style key:value
 * blocks. event_log renders as a tabular section with [N]{fields} header and
 * one CSV row per entry — the most token-efficient form for uniform arrays.
 *
 * CSV quoting follows RFC 4180: cells that contain commas, double-quotes, or
 * newlines are wrapped in double-quotes, with internal double-quotes doubled.
 *
 * See: https://github.com/toon-format/toon
 */

function toonScalar(v: unknown): string {
  if (v === null || v === undefined) return 'null';
  if (typeof v === 'string') return v;
  return String(v);
}

function csvCell(v: string): string {
  if (v.includes(',') || v.includes('"') || v.includes('\n')) {
    return `"${v.replace(/"/g, '""')}"`;
  }
  return v;
}

function flatBlock(label: string, obj: object): string {
  const lines = [`${label}:`];
  for (const [k, v] of Object.entries(obj)) {
    lines.push(`  ${k}: ${toonScalar(v)}`);
  }
  return lines.join('\n');
}

export function toToon(snap: ContextuneSnapshot): string {
  const sections: string[] = [];

  if (snap.marketing_params) {
    sections.push(flatBlock('marketing_params', snap.marketing_params));
  }

  if (snap.device_info) {
    sections.push(flatBlock('device_info', snap.device_info));
  }

  const log = snap.event_log;
  const hasProps = log.some(e => e.properties && Object.keys(e.properties).length > 0);
  const fields = hasProps ? '{name,elapsed_s,properties}' : '{name,elapsed_s}';
  const header = `event_log[${log.length}]${fields}:`;

  const rows = log.map(e => {
    const name = csvCell(e.name);
    const ts = String(e.elapsed_s);
    if (!hasProps) return `  ${name},${ts}`;
    const propsStr =
      e.properties && Object.keys(e.properties).length > 0
        ? csvCell(JSON.stringify(e.properties))
        : '';
    return `  ${name},${ts},${propsStr}`;
  });

  sections.push([header, ...rows].join('\n'));

  return sections.join('\n');
}
